import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Plus,
  UserX,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router';

import { Button } from '../components/Button';
import { SearchInput } from '../components/SearchInput';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { useAuth } from '../features/auth/AuthProvider';
import {
  listRoles,
  listUsers,
  rolesKey,
  usersKey,
  type Member,
  type UserQuery,
} from '../features/users/api';
import { UserDrawer, UserStatusBadge } from '../features/users/UserDrawer';
import { useTimeFormat } from '../lib/useTimeFormat';

const PAGE_SIZES = [25, 50, 100];
const SORTABLE = { name: 'Usuário', last_login: 'Último acesso' } as const;
type SortKey = keyof typeof SORTABLE;

function readQuery(params: URLSearchParams): UserQuery {
  const size = Number(params.get('size'));
  const page = Number(params.get('page'));
  const status = params.get('status');
  return {
    q: params.get('q') ?? '',
    status: status === 'active' || status === 'disabled' ? status : '',
    role: params.get('role') ?? '',
    sort: params.get('sort') ?? 'name',
    page: Number.isInteger(page) && page > 0 ? page : 1,
    size: PAGE_SIZES.includes(size) ? size : 25,
  };
}

type DrawerState = { mode: 'create' } | { mode: 'edit'; member: Member } | null;

export function UsersPage() {
  const { request, can } = useAuth();
  const [params, setParams] = useSearchParams();
  const query = readQuery(params);
  const canManage = can('users.manage');
  const [drawer, setDrawer] = useState<DrawerState>(null);

  const update = (patch: Partial<Record<keyof UserQuery, string>>) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(patch)) {
          if (v) next.set(k, v);
          else next.delete(k);
        }
        if (!('page' in patch)) next.delete('page');
        return next;
      },
      { replace: true },
    );
  };

  const users = useQuery({
    queryKey: [...usersKey, query],
    queryFn: () => listUsers(request, query),
    placeholderData: keepPreviousData,
  });
  const roles = useQuery({
    queryKey: rolesKey,
    queryFn: () => listRoles(request),
    staleTime: 5 * 60_000,
  });

  const filtered = Boolean(query.q || query.status || query.role);
  const total = users.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / query.size));

  return (
    <>
      <PageHeader
        title="Usuários"
        description="Quem acessa a empresa e com qual perfil."
        actions={
          canManage && (
            <Button
              variant="primary"
              onClick={() => {
                setDrawer({ mode: 'create' });
              }}
              disabled={!roles.data}
            >
              <Plus aria-hidden="true" className="size-4" strokeWidth={2} />
              Novo usuário
            </Button>
          )
        }
      />

      <div className="space-y-3 p-4 sm:p-6">
        <div role="search" className="flex flex-wrap items-center gap-2">
          <SearchInput
            label="Buscar por nome ou e-mail"
            value={query.q}
            onChange={(q) => {
              update({ q });
            }}
          />
          <select
            aria-label="Filtrar por status"
            value={query.status}
            onChange={(e) => {
              update({ status: e.target.value });
            }}
            className={selectClass}
          >
            <option value="">Todos os status</option>
            <option value="active">Ativos</option>
            <option value="disabled">Desativados</option>
          </select>
          <select
            aria-label="Filtrar por perfil"
            value={query.role}
            onChange={(e) => {
              update({ role: e.target.value });
            }}
            className={selectClass}
          >
            <option value="">Todos os perfis</option>
            {roles.data
              ?.filter((r) => r.kind !== 'integration')
              .map((r) => (
                <option key={r.key} value={r.key}>
                  {r.name}
                </option>
              ))}
          </select>
          {filtered && (
            <Button
              variant="ghost"
              onClick={() => {
                update({ q: '', status: '', role: '' });
              }}
            >
              Limpar filtros
            </Button>
          )}
        </div>

        <div className="overflow-hidden rounded-lg border border-line bg-surface">
          {users.isError && !users.data ? (
            <EmptyState
              tone="crit"
              icon={CircleAlert}
              title="Não foi possível carregar os usuários"
              description="Verifique a conexão com o servidor e tente novamente."
              action={
                <Button variant="primary" onClick={() => void users.refetch()}>
                  Tentar novamente
                </Button>
              }
            />
          ) : users.data?.items.length === 0 ? (
            filtered ? (
              <EmptyState
                icon={UserX}
                title="Nenhum usuário encontrado"
                description="Nenhum usuário corresponde aos filtros aplicados."
                action={
                  <Button
                    onClick={() => {
                      update({ q: '', status: '', role: '' });
                    }}
                  >
                    Limpar filtros
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={Users}
                title="Nenhum usuário cadastrado"
                description="Cadastre as pessoas que vão operar ou administrar a plataforma."
              />
            )
          ) : (
            <div className="overflow-x-auto">
              <table
                className="w-full min-w-[720px] text-left text-[13px]"
                aria-busy={users.isFetching}
              >
                <caption className="sr-only">
                  Usuários da organização{total ? `, ${total} no total` : ''}
                </caption>
                <thead className="text-2xs tracking-wide text-fg-subtle uppercase">
                  <tr className="border-b border-line">
                    <SortHeader
                      column="name"
                      sort={query.sort}
                      onSort={(sort) => {
                        update({ sort });
                      }}
                    />
                    <th scope="col" className="px-4 py-2 font-medium">
                      Perfil
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Status
                    </th>
                    <SortHeader
                      column="last_login"
                      sort={query.sort}
                      onSort={(sort) => {
                        update({ sort });
                      }}
                    />
                    <th scope="col" className="w-px px-4 py-2">
                      <span className="sr-only">Ações</span>
                    </th>
                  </tr>
                </thead>
                <tbody className={users.isPlaceholderData ? 'opacity-60' : ''}>
                  {!users.data
                    ? Array.from({ length: 6 }, (_, i) => <SkeletonRow key={i} />)
                    : users.data.items.map((m) => (
                        <UserRow
                          key={m.id}
                          member={m}
                          canManage={canManage}
                          ready={Boolean(roles.data)}
                          onEdit={() => {
                            setDrawer({ mode: 'edit', member: m });
                          }}
                        />
                      ))}
                </tbody>
              </table>
            </div>
          )}

          {users.data && total > 0 && (
            <nav
              aria-label="Paginação"
              className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-2.5 text-xs text-fg-muted"
            >
              <span aria-live="polite">
                {(query.page - 1) * query.size + 1}–{Math.min(query.page * query.size, total)} de{' '}
                {total}
              </span>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2">
                  Por página
                  <select
                    value={query.size}
                    onChange={(e) => {
                      update({ size: e.target.value });
                    }}
                    className={`${selectClass} h-7`}
                  >
                    {PAGE_SIZES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    className="h-7 px-2"
                    aria-label="Página anterior"
                    disabled={query.page <= 1}
                    onClick={() => {
                      update({ page: String(query.page - 1) });
                    }}
                  >
                    <ChevronLeft aria-hidden="true" className="size-4" />
                  </Button>
                  <span className="tabular-nums">
                    {query.page} / {pages}
                  </span>
                  <Button
                    variant="ghost"
                    className="h-7 px-2"
                    aria-label="Próxima página"
                    disabled={query.page >= pages}
                    onClick={() => {
                      update({ page: String(query.page + 1) });
                    }}
                  >
                    <ChevronRight aria-hidden="true" className="size-4" />
                  </Button>
                </div>
              </div>
            </nav>
          )}
        </div>
      </div>

      {drawer && roles.data && (
        <UserDrawer
          {...(drawer.mode === 'edit' ? { member: drawer.member } : {})}
          roles={roles.data}
          onClose={() => {
            setDrawer(null);
          }}
        />
      )}
    </>
  );
}

const selectClass =
  'h-8 rounded-md border border-line-strong bg-canvas px-2 text-[13px] text-fg hover:border-fg-subtle/60 focus:border-accent';

function SortHeader({
  column,
  sort,
  onSort,
}: {
  column: SortKey;
  sort: string;
  onSort: (sort: string) => void;
}) {
  const active = sort === column || sort === `-${column}`;
  const desc = sort === `-${column}`;
  const Icon = desc ? ArrowDown : ArrowUp;
  return (
    <th
      scope="col"
      aria-sort={active ? (desc ? 'descending' : 'ascending') : 'none'}
      className="px-4 py-2 font-medium"
    >
      <button
        type="button"
        onClick={() => {
          onSort(active && !desc ? `-${column}` : column);
        }}
        className={`inline-flex items-center gap-1 uppercase hover:text-fg ${active ? 'text-fg-muted' : ''}`}
      >
        {SORTABLE[column]}
        <Icon aria-hidden="true" className={`size-3 ${active ? '' : 'opacity-0'}`} />
      </button>
    </th>
  );
}

function initials(name: string) {
  const parts = name.trim().split(/\s+/);
  return (
    (parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '')
  ).toUpperCase();
}

function UserRow({
  member: m,
  canManage,
  ready,
  onEdit,
}: {
  member: Member;
  canManage: boolean;
  /** False while the role catalogue needed by the editor is loading. */
  ready: boolean;
  onEdit: () => void;
}) {
  const fmt = useTimeFormat();
  return (
    <tr className="border-b border-line last:border-0 hover:bg-surface-hover/50">
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-3">
          <span
            aria-hidden="true"
            className="grid size-7 shrink-0 place-items-center rounded-full bg-surface-hover text-2xs font-semibold text-fg-muted"
          >
            {initials(m.display_name)}
          </span>
          <div className="min-w-0">
            <div className="truncate font-medium text-fg">{m.display_name}</div>
            <div className="truncate text-xs text-fg-subtle">{m.email}</div>
          </div>
        </div>
      </td>
      <td className="px-4 py-2.5">
        <div className="flex flex-wrap gap-1">
          {m.roles.map((r) => (
            <span
              key={r.key}
              className="rounded bg-surface-hover px-1.5 py-0.5 text-2xs text-fg-muted"
            >
              {r.name}
            </span>
          ))}
        </div>
        {m.client && <div className="mt-0.5 truncate text-2xs text-fg-subtle">{m.client.name}</div>}
      </td>
      <td className="px-4 py-2.5">
        <UserStatusBadge member={m} />
      </td>
      <td className="px-4 py-2.5 text-fg-muted">
        {m.last_login_at ? (
          <time dateTime={m.last_login_at} title={fmt.absolute(m.last_login_at)}>
            {fmt.relative(m.last_login_at)}
          </time>
        ) : (
          <span className="text-fg-subtle">Nunca</span>
        )}
      </td>
      <td className="px-4 py-2.5 text-right">
        {canManage && (
          <Button
            variant="ghost"
            className="h-7"
            onClick={onEdit}
            disabled={!ready}
            aria-label={`Editar ${m.display_name}`}
          >
            Editar
          </Button>
        )}
      </td>
    </tr>
  );
}

function SkeletonRow() {
  return (
    <tr aria-hidden="true" className="border-b border-line last:border-0">
      {['w-48', 'w-24', 'w-16', 'w-20', 'w-10'].map((w, i) => (
        <td key={i} className="px-4 py-3.5">
          <div className={`h-3 animate-pulse rounded bg-surface-hover ${w}`} />
        </td>
      ))}
    </tr>
  );
}
